'use client';

import { MutableRefObject } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { IconDownload, IconChevronDown, IconFileSpreadsheet, IconCsv } from '@tabler/icons-react';
import { exportProjectsToXlsx, exportProjectsToCsv, ExportMetadata } from '@/lib/exports/export-projects-xlsx';
import type { ProjectRecord } from '@/lib/projects/project-types';

interface ExportDropdownProps {
  getRows: () => ProjectRecord[];
  isLoadingRef: MutableRefObject<boolean>;
  metadata?: ExportMetadata;
}

export function ExportDropdown({ getRows, isLoadingRef, metadata }: ExportDropdownProps) {
  const handleExport = (format: 'xlsx' | 'csv') => {
    if (isLoadingRef.current) return;
    const rows = getRows();
    if (rows.length === 0) {
      toast('No projects to export');
      return;
    }
    if (format === 'xlsx') {
      const { exportedCount, fileName } = exportProjectsToXlsx({ rows, metadata });
      toast.success(`Exported ${exportedCount} projects to Excel`, { description: fileName });
    } else {
      const { exportedCount, fileName } = exportProjectsToCsv({ rows, metadata });
      toast.success(`Exported ${exportedCount} projects as CSV`, { description: fileName });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <IconDownload className="size-4 mr-1.5" />
          Export
          <IconChevronDown className="size-3.5 ml-1 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport('xlsx')}>
          <IconFileSpreadsheet className="size-4 mr-2" />
          Export Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('csv')}>
          <IconCsv className="size-4 mr-2" />
          Export CSV (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
